import { type ChangeEvent, type FC, type KeyboardEvent, useState } from "react";
import { MetaAdsDistanceUnit } from "shared";
import { useDebounceCallback, useEventCallback } from "usehooks-ts";
import { Api } from "../../api.config";
import type { ButtonProps } from "../../components/Button";
import SearchBox from "../../components/SearchBox";
import { useGooglePlaceAutocomplete } from "../../settings/useIntegrations";

interface Props {
	canAddPin: boolean;
	onAddPin: (pin: {
		label: string;
		latitude: number;
		longitude: number;
		radius: number;
		distanceUnit: MetaAdsDistanceUnit;
	}) => void;
}

export const LocationSearch: FC<Props> = ({ canAddPin, onAddPin }) => {
	const [query, setQuery] = useState("");
	const [resolving, setResolving] = useState(false);

	const {
		data: placesData,
		refetch: refetchPlaces,
		isFetching: placesLoading,
	} = useGooglePlaceAutocomplete(query);

	const debounceAutocomplete = useDebounceCallback(async () => {
		await refetchPlaces();
	}, 500);

	const handleInput = useEventCallback(
		async (e: ChangeEvent<HTMLInputElement>) => {
			setQuery(e.target.value);
			if (e.target.value.length > 0) {
				await debounceAutocomplete();
			}
		},
	);

	const handleKeyDown = useEventCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Enter") {
				e.preventDefault();
				const first = placesData?.[0]?.placePrediction;
				if (first?.placeId) {
					handleSelect(first.placeId, first.text?.text ?? "Unknown location");
				}
			}
		},
	);

	const handleSelect = useEventCallback(
		async (placeId: string, label: string) => {
			if (!canAddPin) return;
			setResolving(true);
			try {
				const res = await Api.GooglePlacesDetails(placeId);
				onAddPin({
					label: res.data.formattedAddress || label,
					latitude: res.data.latitude,
					longitude: res.data.longitude,
					radius: 10,
					distanceUnit: MetaAdsDistanceUnit.MILE,
				});
				setQuery("");
			} finally {
				setResolving(false);
			}
		},
	);

	return (
		<SearchBox
			loading={placesLoading || resolving}
			inputProps={{
				placeholder: canAddPin
					? "Search for an address..."
					: "Max 10 locations reached",
				value: query,
				onChange: handleInput,
				onKeyDown: handleKeyDown,
				disabled: !canAddPin,
			}}
			autocompleteItems={placesData?.map(
				(place) =>
					({
						full: true,
						icon: "add_location",
						variant: "ghost",
						iconSize: 24,
						children: <p>{place?.placePrediction?.text?.text}</p>,
						onClick: () => {
							const placeId = place?.placePrediction?.placeId;
							const label =
								place?.placePrediction?.text?.text ?? "Unknown location";
							if (placeId) {
								handleSelect(placeId, label);
							}
						},
					}) as ButtonProps,
			)}
		/>
	);
};
